import { useState, useCallback } from 'react';
import type { MenuItem } from '../../../types/menu';
import type { NavigationActionHandler } from '../../../types/base';

interface UseDropdownNavigationProps {
  onAction?: NavigationActionHandler;
}

export const useDropdownNavigation = ({ onAction }: UseDropdownNavigationProps = {}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Handle click events for dropdown items
  const handleItemClick = useCallback((item: MenuItem) => {
    // Handle custom action handlers (e.g., modal triggers, API calls)
    if (item.metadata?.handler && onAction) {
      onAction(item.metadata.handler, item.metadata.params);
      return;
    }
    
    // Handle navigation to URLs
    if (item.href) {
      if (item.external) {
        // Open external links in new tab
        window.open(item.href, '_blank', 'noopener,noreferrer');
      } else {
        // Navigate to internal URLs
        window.location.href = item.href;
      }
    }
  }, [onAction]);

  // Toggle expanded state for expandable items
  const toggleExpandedItem = useCallback((itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  return {
    expandedItems,
    handleItemClick,
    toggleExpandedItem,
  };
}; 